// One error type for every HTTP boundary in the agent UI.
//
// The rule this file exists to enforce: a response BODY never becomes
// Error.message. Express answers an unknown route with a whole HTML document,
// and the old `new Error(`${res.status} ${await res.text()}`)` put that document
// straight on screen — a tester saw "404 <!DOCTYPE html>…Cannot GET
// /agent/tickets" rendered verbatim in the panel. GCS signed uploads are worse:
// they answer with an XML <Error> document.
//
// So: the body is logged to the console (where it is actually useful) and kept
// on `.detail` for an optional collapsible "Details"; what the agent reads is
// short copy chosen from the status code.

/** How much of a raw body we keep on the error object for the Details affordance. */
const DETAIL_MAX = 2000;
/** How much of a raw body we print to the console. */
const LOG_MAX = 500;

/** Status 0 is our stand-in for "the request never got an answer" (network/CORS). */
const STATUS_COPY: Record<number, string> = {
  0: 'Network problem — check your connection and try again.',
  400: "That request wasn't valid.",
  401: 'Your session expired — sign in again.',
  403: "You don't have permission to do that.",
  404: "That feature isn't available on this server yet.",
  409: 'That conflicts with a change someone else just made.',
  429: 'Too many requests — wait a moment and try again.',
};

export function copyForStatus(status: number): string {
  const exact = STATUS_COPY[status];
  if (exact) return exact;
  if (status >= 500) return "The server couldn't complete that. Try again shortly.";
  if (status >= 400) return `Request failed (${status}).`;
  return `Unexpected response (${status}).`;
}

export class ApiError extends Error {
  readonly status: number;
  /** Provider/policy code when the server sent one (e.g. '190' from Meta). */
  readonly code: string | null;
  /** Raw (truncated) body for a collapsible "Details". Never shown by default. */
  readonly detail: string | null;

  constructor(status: number, message: string, opts?: { code?: string | null; detail?: string | null }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = opts?.code ?? null;
    this.detail = opts?.detail ?? null;
    // Keeps `instanceof ApiError` true even if the bundler ever down-levels
    // classes — tolerantFetch()'s 404 probe is load-bearing on it.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** A server string is only fit to show if it reads like a sentence, not a document. */
function looksLikeMessage(s: unknown): s is string {
  return typeof s === 'string' && s.length > 0 && s.length <= 200 && !s.includes('<') && !s.includes('\n');
}

function firstString(body: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = body[k];
    if (looksLikeMessage(v)) return v;
  }
  return null;
}

/**
 * Build an ApiError from a failed Response. JSON bodies are ours, so a short
 * `error`/`detail`/`message` string from one is worth showing; anything else
 * (HTML, XML, plain text) is logged and hidden behind the status copy.
 */
export async function toApiError(res: Response, path: string): Promise<ApiError> {
  const ctype = res.headers.get('content-type') ?? '';
  const fallback = copyForStatus(res.status);

  if (ctype.includes('json')) {
    const raw = await res.text().catch(() => '');
    try {
      const body = (raw ? JSON.parse(raw) : null) as Record<string, unknown> | null;
      if (body && typeof body === 'object') {
        const message = firstString(body, ['error', 'message', 'detail', 'reason']);
        const code = body.code ?? body.errorCode ?? null;
        return new ApiError(res.status, message ?? fallback, {
          code: code === null || code === undefined ? null : String(code),
          detail: truncate(raw, DETAIL_MAX),
        });
      }
    } catch {
      // Content-Type lied. Fall through to the opaque-body path below.
    }
    if (raw) console.error(`[api] ${res.status} ${path} — malformed JSON body`, truncate(raw, LOG_MAX));
    return new ApiError(res.status, fallback, { detail: raw ? truncate(raw, DETAIL_MAX) : null });
  }

  const text = await res.text().catch(() => '');
  if (text) console.error(`[api] ${res.status} ${path}`, truncate(text, LOG_MAX));
  return new ApiError(res.status, fallback, { detail: text ? truncate(text, DETAIL_MAX) : null });
}

/** A 2xx that isn't the JSON we asked for — a proxy/login page, usually. */
export function malformedJsonError(status: number, path: string, body: string): ApiError {
  console.error(`[api] ${status} ${path} — expected JSON, got`, truncate(body, LOG_MAX));
  return new ApiError(status, "The server sent a response we couldn't read.", {
    detail: truncate(body, DETAIL_MAX),
  });
}

/**
 * XHR sibling of toApiError, for the upload progress path. Same rule: the
 * responseText (a GCS XML <Error> doc, typically) never reaches Error.message.
 */
export function xhrApiError(xhr: XMLHttpRequest, path: string): ApiError {
  const ctype = xhr.getResponseHeader('content-type') ?? '';
  const raw = typeof xhr.responseText === 'string' ? xhr.responseText : '';
  const fallback = copyForStatus(xhr.status);

  if (ctype.includes('json') && raw) {
    try {
      const body = JSON.parse(raw) as Record<string, unknown>;
      const message = firstString(body, ['error', 'message', 'detail', 'reason']);
      if (message) return new ApiError(xhr.status, message, { detail: truncate(raw, DETAIL_MAX) });
    } catch {
      // fall through
    }
  }
  if (raw) console.error(`[api] ${xhr.status} ${path}`, truncate(raw, LOG_MAX));
  return new ApiError(xhr.status, fallback, { detail: raw ? truncate(raw, DETAIL_MAX) : null });
}

/** Leading "404 …" / "500 …" from the pre-ApiError helpers we are replacing. */
const LEGACY_STATUS_PREFIX = /^(\d{3})\b/;

/** What to render for a caught value. Use this instead of `e.message` at call sites. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) {
    const legacy = LEGACY_STATUS_PREFIX.exec(e.message);
    if (legacy) return copyForStatus(Number(legacy[1]));
    return e.message;
  }
  return String(e);
}

/** Raw body behind a caught value, when there is one worth offering as "Details". */
export function errorDetail(e: unknown): string | null {
  return e instanceof ApiError ? e.detail : null;
}
